import { useEffect, useRef, useState } from "react";
import {
  Bold,
  Italic,
  Link as LinkIcon,
  Type,
  Heading1,
  MousePointerClick,
  Image as ImageIcon,
  Minus,
  MoveVertical,
  Code2,
  Plus,
  ChevronUp,
  ChevronDown,
  Copy,
  Trash2,
  Upload,
  AlignLeft,
  AlignCenter,
  AlignRight,
  X,
} from "lucide-react";
import {
  wrapEmailContent,
  escapeHtmlEntities,
  BRAND_COLORS,
  type EmailLayoutVariant,
} from "@/lib/email-layout";

/**
 * Editor de campañas por BLOQUES (estilo Mailchimp/Brevo), a medida con
 * TailwindCSS puro (sin librerías de edición nuevas). Sustituye a
 * `VisualEmailEditor.tsx` SOLO en `EmailCampaigns.tsx` (campañas del
 * hostelero): `AdminCampaigns.tsx` sigue usando el editor antiguo, que no se
 * toca ni se borra.
 *
 * ARQUITECTURA (resumen; ver README de esta sección para el detalle):
 * 1. `value`/`onChange` son el documento COMPLETO (`EmailCampaign.htmlContent`,
 *    el que produce `wrapEmailContent()`), igual que `VisualEmailEditor`. Este
 *    componente NUNCA inventa su propio envoltorio: extrae el fragmento
 *    editable (`extractEditableFragment`), lo parsea a `EmailBlock[]`
 *    (`parseFragmentToBlocks`) y, en cada cambio, vuelve a serializar a HTML
 *    (`buildBlocksFragment` + `wrapEmailContent`).
 * 2. Cada bloque se serializa como una `<table>` (o `<div>` para el bloque
 *    HTML libre) con `data-rm-block="tipo"` y los `data-rm-*` mínimos para
 *    reconstruirlo sin pérdidas; el texto/URL/imagen en sí se lee siempre del
 *    DOM real (nunca de un JSON aparte — así el `src` de una imagen que el
 *    backend re-aloja al guardar se recupera correctamente la próxima vez que
 *    se abre la campaña).
 * 3. Si el fragmento no tiene marcadores de bloque reconocidos (campaña
 *    vieja editada con el editor antiguo, contenido de la IA, HTML pegado a
 *    mano) se trata como UN único bloque "HTML libre": nunca se rompe ni se
 *    pierde contenido, simplemente no se puede editar bloque a bloque hasta
 *    que el usuario lo reconstruya con los bloques nuevos.
 */

// ============================================================================
// TIPOS
// ============================================================================

export type BlockAlign = "left" | "center" | "right";

export interface HeadingBlock {
  id: string;
  type: "heading";
  text: string;
  size: "s" | "m" | "l";
  color: string;
  align: BlockAlign;
}

export interface TextBlock {
  id: string;
  type: "text";
  /** HTML saneado: solo `b/strong/i/em/u/br/a` (ver `sanitizeInlineHtml`). */
  html: string;
}

export interface ButtonBlock {
  id: string;
  type: "button";
  text: string;
  /** URL libre o marcador `{{variable}}` literal, sin validar como URL. */
  href: string;
  bgColor: string;
  textColor: string;
  align: BlockAlign;
  fullWidth: boolean;
  /** px, 0-24. Outlook de escritorio lo ignora (limitación conocida de email). */
  radius: number;
}

export interface ImageBlock {
  id: string;
  type: "image";
  /** `data:` URI (subida pendiente de alojar) o URL ya alojada. */
  src: string;
  alt: string;
  widthPercent: number;
  align: BlockAlign;
  /** "" = sin enlace. */
  href: string;
}

export interface DividerBlock {
  id: string;
  type: "divider";
  color: string;
  thickness: number;
}

export interface SpacerBlock {
  id: string;
  type: "spacer";
  height: number;
}

export interface HtmlBlock {
  id: string;
  type: "html";
  html: string;
}

export type EmailBlock =
  | HeadingBlock
  | TextBlock
  | ButtonBlock
  | ImageBlock
  | DividerBlock
  | SpacerBlock
  | HtmlBlock;

export interface EmailVariableInfo {
  token: string;
  description: string;
  highlight?: boolean;
}

interface EmailBlockEditorProps {
  /** Documento completo (`EmailCampaign.htmlContent`), nunca solo el fragmento. */
  value: string;
  onChange: (html: string) => void;
  brandName: string;
  variant?: EmailLayoutVariant;
  logoUrl?: string | null;
  /** Guía de variables de la página (`CAMPAIGN_VARIABLES`): se reutiliza tal
   * cual para "insertar variable" en el texto y se filtra (tokens que
   * contienen "Url") para las sugerencias de los campos de enlace. */
  variables: EmailVariableInfo[];
}

// ============================================================================
// MARCADORES Y ENVOLTORIO (mismo contrato que `VisualEmailEditor.tsx`)
// ============================================================================

const CONTENT_MARKER_START = "<!--rm-content-start-->";
const CONTENT_MARKER_END = "<!--rm-content-end-->";

const RECOGNIZED_BLOCK_TYPES = new Set<EmailBlock["type"]>([
  "heading",
  "text",
  "button",
  "image",
  "divider",
  "spacer",
  "html",
]);

const HEADING_SIZE_PX: Record<HeadingBlock["size"], number> = {
  s: 18,
  m: 22,
  l: 28,
};

const BLOCK_LABELS: Record<EmailBlock["type"], string> = {
  heading: "Título",
  text: "Texto",
  button: "Botón",
  image: "Imagen",
  divider: "Separador",
  spacer: "Espaciador",
  html: "HTML libre",
};

let idSeq = 0;
function makeId(): string {
  idSeq += 1;
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `blk-${Date.now()}-${idSeq}`;
}

export function createBlock(type: EmailBlock["type"]): EmailBlock {
  const id = makeId();
  switch (type) {
    case "heading":
      return { id, type, text: "Nuevo título", size: "m", color: "#111111", align: "left" };
    case "text":
      return { id, type, html: "Escribe aquí tu texto…" };
    case "button":
      return {
        id,
        type,
        text: "Botón de acción",
        href: "{{bookingUrl}}",
        bgColor: BRAND_COLORS.primaryDark,
        textColor: "#ffffff",
        align: "center",
        fullWidth: false,
        radius: 4,
      };
    case "image":
      return { id, type, src: "", alt: "", widthPercent: 100, align: "center", href: "" };
    case "divider":
      return { id, type, color: "#e5e7eb", thickness: 1 };
    case "spacer":
      return { id, type, height: 24 };
    case "html":
      return { id, type, html: "<p>Contenido HTML libre</p>" };
  }
}

function makeHtmlBlock(html: string): HtmlBlock {
  return { id: makeId(), type: "html", html };
}

// ============================================================================
// SERIALIZACIÓN: Block[] → HTML compatible con email
// ============================================================================

function renderHeading(b: HeadingBlock): string {
  const px = HEADING_SIZE_PX[b.size];
  const lh = px + 8;
  return `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" data-rm-block="heading" data-rm-size="${b.size}" data-rm-color="${b.color}" data-rm-align="${b.align}" style="margin:0 0 16px;">
<tr><td align="${b.align}">
<p style="margin:0;font-family:Arial,Helvetica,sans-serif;font-weight:bold;font-size:${px}px;line-height:${lh}px;color:${b.color};text-align:${b.align};">${escapeHtmlEntities(b.text) || "&nbsp;"}</p>
</td></tr>
</table>`;
}

function renderText(b: TextBlock): string {
  return `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" data-rm-block="text" style="margin:0 0 16px;">
<tr><td style="font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:24px;color:#333333;">
${b.html || "<br>"}
</td></tr>
</table>`;
}

function renderButton(b: ButtonBlock): string {
  const radius = Math.max(0, Math.min(24, b.radius));
  const widthAttr = b.fullWidth ? ` width="100%"` : "";
  const href = escapeHtmlEntities(b.href || "#");
  const linkStyle = `display:block;font-family:Arial,Helvetica,sans-serif;font-size:16px;font-weight:bold;color:${b.textColor};text-decoration:none;border-radius:${radius}px;${b.fullWidth ? "text-align:center;" : ""}`;
  return `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" data-rm-block="button" data-rm-align="${b.align}" data-rm-bg="${b.bgColor}" data-rm-color="${b.textColor}" data-rm-full="${b.fullWidth ? "1" : "0"}" data-rm-radius="${radius}" style="margin:16px 0;">
<tr><td align="${b.align}">
<table role="presentation" border="0" cellpadding="0" cellspacing="0"${widthAttr}><tr>
<td bgcolor="${b.bgColor}" align="center" style="padding:14px 28px;border-radius:${radius}px;">
<a href="${href}" target="_blank" style="${linkStyle}">${escapeHtmlEntities(b.text) || "Botón"}</a>
</td>
</tr></table>
</td></tr>
</table>`;
}

function renderImage(b: ImageBlock): string {
  const px = Math.max(20, Math.round(552 * (b.widthPercent / 100)));
  // `margin` según alineación: `auto` centra un elemento block en los clientes
  // que respetan CSS (Gmail/Apple Mail) y anularía el `align` del td si se
  // emitiera siempre; izquierda/derecha van con margin dirigido.
  const margin =
    b.align === "center"
      ? "margin:0 auto;"
      : b.align === "right"
        ? "margin:0 0 0 auto;"
        : "margin:0;";
  const imgTag = `<img src="${escapeHtmlEntities(b.src)}" alt="${escapeHtmlEntities(b.alt)}" width="${px}" style="display:block;width:100%;max-width:${px}px;height:auto;border:0;${margin}" />`;
  const content = b.href
    ? `<a href="${escapeHtmlEntities(b.href)}" target="_blank">${imgTag}</a>`
    : imgTag;
  return `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" data-rm-block="image" data-rm-align="${b.align}" data-rm-width="${b.widthPercent}" style="margin:0 0 16px;">
<tr><td align="${b.align}">${content}</td></tr>
</table>`;
}

function renderDivider(b: DividerBlock): string {
  return `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" data-rm-block="divider" data-rm-color="${b.color}" data-rm-thickness="${b.thickness}" style="margin:16px 0;">
<tr><td style="font-size:0;line-height:0;border-top:${b.thickness}px solid ${b.color};">&nbsp;</td></tr>
</table>`;
}

function renderSpacer(b: SpacerBlock): string {
  return `<table role="presentation" width="100%" border="0" cellpadding="0" cellspacing="0" data-rm-block="spacer" data-rm-height="${b.height}">
<tr><td style="height:${b.height}px;line-height:${b.height}px;font-size:0;">&nbsp;</td></tr>
</table>`;
}

function renderHtmlBlock(b: HtmlBlock): string {
  return `<div data-rm-block="html">${b.html}</div>`;
}

function renderBlock(b: EmailBlock): string {
  switch (b.type) {
    case "heading":
      return renderHeading(b);
    case "text":
      return renderText(b);
    case "button":
      return renderButton(b);
    case "image":
      return renderImage(b);
    case "divider":
      return renderDivider(b);
    case "spacer":
      return renderSpacer(b);
    case "html":
      return renderHtmlBlock(b);
  }
}

export function blocksToHtml(blocks: EmailBlock[]): string {
  return blocks.map(renderBlock).join("\n");
}

/** Fragmento editable completo (bloques + marcadores), listo para pasar como
 * `content` a `wrapEmailContent()`. Lo usan tanto este componente como las
 * plantillas predefinidas de `EmailCampaigns.tsx`. */
export function buildBlocksFragment(blocks: EmailBlock[]): string {
  return `${CONTENT_MARKER_START}${blocksToHtml(blocks)}${CONTENT_MARKER_END}`;
}

// ============================================================================
// PARSEO: HTML → Block[] (round-trip sin pérdidas)
// ============================================================================

/**
 * Localiza el fragmento editable dentro del documento COMPLETO. Orden de
 * intentos: (1) marcadores `rm-content-*` — lo que este propio editor y
 * `VisualEmailEditor` ya usan; (2) `td[data-rm-content-cell]` — la celda de
 * contenido que pone `wrapEmailContent()`, presente en CUALQUIER documento
 * generado por esa función aunque nunca se haya pasado por un editor visual
 * (plantillas/IA recién elegidas); (3) formato heredado `.content` (editor
 * visual anterior a `VisualEmailEditor`); (4) `<body>` a secas; (5) el valor
 * tal cual, como último recurso — nunca lanza ni pierde contenido.
 */
function extractEditableFragment(html: string): string {
  if (!html) return "";

  const markerStart = html.indexOf(CONTENT_MARKER_START);
  if (markerStart !== -1) {
    const from = markerStart + CONTENT_MARKER_START.length;
    const end = html.indexOf(CONTENT_MARKER_END, from);
    if (end !== -1) return html.slice(from, end);
  }

  if (typeof DOMParser !== "undefined") {
    try {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const cell = doc.querySelector('[data-rm-content-cell="1"]');
      if (cell) return cell.innerHTML;
      const legacy = doc.querySelector(".content");
      if (legacy) return legacy.innerHTML;
    } catch {
      // sigue a los fallbacks de abajo
    }
  }

  const bodyMatch = html.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  if (bodyMatch) return bodyMatch[1];

  return html;
}

function parseFragmentToBlocks(fragment: string): EmailBlock[] {
  if (!fragment || !fragment.trim()) return [];
  if (typeof DOMParser === "undefined") return [makeHtmlBlock(fragment)];

  let root: HTMLElement | null;
  try {
    const doc = new DOMParser().parseFromString(
      `<div id="rm-root">${fragment}</div>`,
      "text/html",
    );
    root = doc.getElementById("rm-root");
  } catch {
    return [makeHtmlBlock(fragment)];
  }
  if (!root) return [makeHtmlBlock(fragment)];

  const hasStrayText = Array.from(root.childNodes).some(
    (n) => n.nodeType === Node.TEXT_NODE && !!n.textContent?.trim(),
  );
  const children = Array.from(root.children) as HTMLElement[];
  const allRecognized =
    children.length > 0 &&
    !hasStrayText &&
    children.every((el) =>
      RECOGNIZED_BLOCK_TYPES.has(
        (el.getAttribute("data-rm-block") || "") as EmailBlock["type"],
      ),
    );

  if (!allRecognized) return [makeHtmlBlock(fragment)];

  const blocks = children
    .map((el) => parseBlockElement(el))
    .filter((b): b is EmailBlock => b !== null);
  return blocks.length > 0 ? blocks : [makeHtmlBlock(fragment)];
}

function parseBlockElement(el: HTMLElement): EmailBlock | null {
  const type = el.getAttribute("data-rm-block");
  switch (type) {
    case "heading": {
      const p = el.querySelector("p,h1,h2,h3");
      const size = el.getAttribute("data-rm-size");
      return {
        id: makeId(),
        type: "heading",
        text: p?.textContent?.trim() || "",
        size: size === "s" || size === "l" ? size : "m",
        color: el.getAttribute("data-rm-color") || "#111111",
        align: parseAlign(el.getAttribute("data-rm-align")),
      };
    }
    case "text": {
      const td = el.querySelector("td");
      return { id: makeId(), type: "text", html: sanitizeInlineHtml(td?.innerHTML || "") };
    }
    case "button": {
      const a = el.querySelector("a");
      return {
        id: makeId(),
        type: "button",
        text: a?.textContent?.trim() || "Botón",
        href: a?.getAttribute("href") || "",
        bgColor: el.getAttribute("data-rm-bg") || BRAND_COLORS.primaryDark,
        textColor: el.getAttribute("data-rm-color") || "#ffffff",
        align: parseAlign(el.getAttribute("data-rm-align"), "center"),
        fullWidth: el.getAttribute("data-rm-full") === "1",
        radius: Number(el.getAttribute("data-rm-radius")) || 0,
      };
    }
    case "image": {
      const img = el.querySelector("img");
      const link = img?.closest("a");
      return {
        id: makeId(),
        type: "image",
        src: img?.getAttribute("src") || "",
        alt: img?.getAttribute("alt") || "",
        widthPercent: Number(el.getAttribute("data-rm-width")) || 100,
        align: parseAlign(el.getAttribute("data-rm-align"), "center"),
        href: link?.getAttribute("href") || "",
      };
    }
    case "divider":
      return {
        id: makeId(),
        type: "divider",
        color: el.getAttribute("data-rm-color") || "#e5e7eb",
        thickness: Number(el.getAttribute("data-rm-thickness")) || 1,
      };
    case "spacer":
      return {
        id: makeId(),
        type: "spacer",
        height: Number(el.getAttribute("data-rm-height")) || 24,
      };
    case "html":
      return { id: makeId(), type: "html", html: el.innerHTML };
    default:
      return null;
  }
}

function parseAlign(value: string | null, fallback: BlockAlign = "left"): BlockAlign {
  return value === "left" || value === "center" || value === "right" ? value : fallback;
}

/** Solo `b/strong/i/em/u/br/a` sobreviven; el resto se "desenvuelve"
 * (se conservan sus hijos, se descarta la etiqueta). Los `<a>` se normalizan
 * a `href` + estilo de enlace inline (nada de clases: no viajan al email). */
const TEXT_ALLOWED_TAGS = new Set(["B", "STRONG", "I", "EM", "U", "BR", "A"]);

/** Etiquetas de BLOQUE: al desenvolverlas hay que conservar el salto de línea
 * que representaban (Enter en contentEditable crea `<div>` por línea; el
 * contenido pegado de Word/web trae `<p>`). Sin esto, "hola<div>mundo</div>"
 * se serializaba como "holamundo": las líneas se veían separadas en el editor
 * pero llegaban fundidas al email. */
const TEXT_BLOCK_LEVEL_TAGS = new Set([
  "DIV", "P", "LI", "UL", "OL", "H1", "H2", "H3", "H4", "H5", "H6",
  "BLOCKQUOTE", "PRE", "TABLE", "TR", "SECTION", "ARTICLE",
]);

function sanitizeInlineHtml(html: string): string {
  if (typeof DOMParser === "undefined") return html;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  const root = doc.body.firstElementChild as HTMLElement | null;
  if (!root) return "";

  for (let pass = 0; pass < 8; pass++) {
    const disallowed = Array.from(root.querySelectorAll("*")).filter(
      (el) => !TEXT_ALLOWED_TAGS.has(el.tagName),
    );
    if (disallowed.length === 0) break;
    disallowed.forEach((el) => {
      if (!root.contains(el)) return;
      if (TEXT_BLOCK_LEVEL_TAGS.has(el.tagName) && el.previousSibling) {
        el.before(doc.createElement("br"));
      }
      el.replaceWith(...Array.from(el.childNodes));
    });
  }

  root.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (el.tagName === "A" && attr.name === "href") return;
      el.removeAttribute(attr.name);
    });
    if (el.tagName === "A") {
      el.setAttribute("target", "_blank");
      el.setAttribute("style", "color:#0284c7;text-decoration:underline;");
    }
  });

  return root.innerHTML;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export default function EmailBlockEditor({
  value,
  onChange,
  brandName,
  variant = "restaurant",
  logoUrl,
  variables,
}: EmailBlockEditorProps) {
  const lastValueRef = useRef<string | null>(value);
  const [blocks, setBlocks] = useState<EmailBlock[]>(() =>
    parseFragmentToBlocks(extractEditableFragment(value)),
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Re-sincroniza cuando `value` cambia por una razón EXTERNA (cambiar de
  // campaña, elegir plantilla, generar con IA, editar en la pestaña HTML y
  // volver a Visual). Si el cambio es el eco de nuestro propio `onChange`, se
  // ignora — mismo patrón que `VisualEmailEditor`.
  useEffect(() => {
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    setBlocks(parseFragmentToBlocks(extractEditableFragment(value)));
    setSelectedId(null);
  }, [value]);

  const emitBlocks = (next: EmailBlock[]) => {
    const html = wrapEmailContent({
      variant,
      brandName,
      logoUrl,
      content: buildBlocksFragment(next),
    });
    lastValueRef.current = html;
    onChange(html);
  };

  const updateBlocks = (next: EmailBlock[]) => {
    setBlocks(next);
    emitBlocks(next);
  };

  const updateBlock = (id: string, patch: Partial<EmailBlock>) => {
    updateBlocks(
      blocks.map((b) => (b.id === id ? ({ ...b, ...patch } as EmailBlock) : b)),
    );
  };

  const insertBlock = (index: number, type: EmailBlock["type"]) => {
    const nb = createBlock(type);
    updateBlocks([...blocks.slice(0, index), nb, ...blocks.slice(index)]);
    setSelectedId(nb.id);
  };

  const removeBlock = (id: string) => {
    updateBlocks(blocks.filter((b) => b.id !== id));
    setSelectedId((current) => (current === id ? null : current));
  };

  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex((b) => b.id === id);
    if (idx === -1) return;
    const copy = { ...blocks[idx], id: makeId() } as EmailBlock;
    updateBlocks([...blocks.slice(0, idx + 1), copy, ...blocks.slice(idx + 1)]);
    setSelectedId(copy.id);
  };

  const moveBlock = (id: string, dir: -1 | 1) => {
    const idx = blocks.findIndex((b) => b.id === id);
    const swapIdx = idx + dir;
    if (idx === -1 || swapIdx < 0 || swapIdx >= blocks.length) return;
    const next = [...blocks];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    updateBlocks(next);
  };

  const selected = blocks.find((b) => b.id === selectedId) ?? null;
  const urlVariables = variables.filter((v) => v.token.toLowerCase().includes("url"));

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white lg:flex">
      {/* Lienzo */}
      <div
        className="flex-1 min-w-0 bg-gray-100 p-3 md:p-6"
        onClick={() => setSelectedId(null)}
      >
        <div
          className="mx-auto bg-white shadow-sm rounded-md overflow-hidden"
          style={{ maxWidth: 600 }}
        >
          <BlockInserter onInsert={(t) => insertBlock(0, t)} />
          {blocks.map((b, i) => (
            <div key={b.id}>
              <BlockRow
                block={b}
                selected={b.id === selectedId}
                onSelect={() => setSelectedId(b.id)}
                onChange={(patch) => updateBlock(b.id, patch)}
                onDuplicate={() => duplicateBlock(b.id)}
                onDelete={() => removeBlock(b.id)}
                onMoveUp={() => moveBlock(b.id, -1)}
                onMoveDown={() => moveBlock(b.id, 1)}
                isFirst={i === 0}
                isLast={i === blocks.length - 1}
                variables={variables}
              />
              <BlockInserter onInsert={(t) => insertBlock(i + 1, t)} />
            </div>
          ))}
          {blocks.length === 0 && (
            <p className="px-6 py-10 text-center text-sm text-gray-400">
              Este email está vacío. Añade tu primer bloque con el botón{" "}
              <span className="font-semibold">+</span> de arriba.
            </p>
          )}
        </div>
      </div>

      {/* Panel de propiedades — escritorio */}
      <div className="hidden lg:block w-80 flex-shrink-0 border-l border-gray-200 bg-white overflow-y-auto">
        <PropertiesPanelContent
          block={selected}
          onChange={(patch) => selected && updateBlock(selected.id, patch)}
          urlVariables={urlVariables}
        />
      </div>

      {/* Panel de propiedades — hoja inferior en móvil. Los bloques de TEXTO
          se editan inline (su hoja solo mostraba un texto de ayuda) y abrirla
          tapaba el contentEditable con el backdrop: quedaba imposible escribir
          en móvil. Para el resto de tipos la hoja es el único panel táctil. */}
      {selected && selected.type !== "text" && (
        <div className="lg:hidden fixed inset-0 z-50 flex items-end">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setSelectedId(null)}
          />
          <div className="relative w-full max-h-[75dvh] bg-white rounded-t-2xl overflow-y-auto shadow-xl">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-900">
                {BLOCK_LABELS[selected.type]}
              </span>
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                className="p-1 rounded hover:bg-gray-100 text-gray-500"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <PropertiesPanelContent
              block={selected}
              onChange={(patch) => updateBlock(selected.id, patch)}
              urlVariables={urlVariables}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FILA DE BLOQUE (lienzo)
// ============================================================================

interface BlockRowProps {
  block: EmailBlock;
  selected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  isFirst: boolean;
  isLast: boolean;
  variables: EmailVariableInfo[];
}

function BlockRow({
  block,
  selected,
  onSelect,
  onChange,
  onDuplicate,
  onDelete,
  onMoveUp,
  onMoveDown,
  isFirst,
  isLast,
  variables,
}: BlockRowProps) {
  let canvas: React.ReactNode;
  switch (block.type) {
    case "heading":
      canvas = <HeadingCanvas block={block} />;
      break;
    case "text":
      canvas = (
        <TextCanvas
          block={block}
          onChange={onChange as (patch: Partial<TextBlock>) => void}
          variables={variables}
        />
      );
      break;
    case "button":
      canvas = <ButtonCanvas block={block} />;
      break;
    case "image":
      canvas = <ImageCanvas block={block} />;
      break;
    case "divider":
      canvas = <DividerCanvas block={block} />;
      break;
    case "spacer":
      canvas = <SpacerCanvas block={block} />;
      break;
    case "html":
      canvas = <HtmlCanvas block={block} />;
      break;
  }

  return (
    <div
      className={`relative group border-2 ${selected ? "border-primary-400" : "border-transparent hover:border-primary-200"} transition-colors`}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <div
        className={`absolute -top-3 right-2 z-10 items-center gap-0.5 bg-white border border-gray-200 rounded-lg shadow-sm px-1 py-0.5 ${
          selected ? "flex" : "hidden group-hover:flex"
        }`}
      >
        <ToolbarIconBtn title="Subir" disabled={isFirst} onClick={onMoveUp}>
          <ChevronUp className="w-3.5 h-3.5" />
        </ToolbarIconBtn>
        <ToolbarIconBtn title="Bajar" disabled={isLast} onClick={onMoveDown}>
          <ChevronDown className="w-3.5 h-3.5" />
        </ToolbarIconBtn>
        <ToolbarIconBtn title="Duplicar" onClick={onDuplicate}>
          <Copy className="w-3.5 h-3.5" />
        </ToolbarIconBtn>
        <ToolbarIconBtn title="Eliminar" danger onClick={onDelete}>
          <Trash2 className="w-3.5 h-3.5" />
        </ToolbarIconBtn>
      </div>
      <div className="px-4 py-2.5">{canvas}</div>
    </div>
  );
}

function ToolbarIconBtn({
  title,
  onClick,
  disabled,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      className={`p-1 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed ${
        danger ? "text-red-500 hover:bg-red-50" : "text-gray-500"
      }`}
    >
      {children}
    </button>
  );
}

// ============================================================================
// "+" INSERTAR BLOQUE
// ============================================================================

const BLOCK_TYPE_META: Array<{
  type: EmailBlock["type"];
  label: string;
  Icon: typeof Type;
}> = [
  { type: "heading", label: "Título", Icon: Heading1 },
  { type: "text", label: "Texto", Icon: Type },
  { type: "button", label: "Botón", Icon: MousePointerClick },
  { type: "image", label: "Imagen", Icon: ImageIcon },
  { type: "divider", label: "Separador", Icon: Minus },
  { type: "spacer", label: "Espaciador", Icon: MoveVertical },
  { type: "html", label: "HTML libre", Icon: Code2 },
];

function BlockInserter({
  onInsert,
}: {
  onInsert: (type: EmailBlock["type"]) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative flex items-center justify-center h-4">
      <div className="absolute inset-x-4 h-px bg-transparent hover:bg-primary-100" />
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="relative z-10 w-5 h-5 flex items-center justify-center rounded-full bg-white border border-gray-300 text-gray-400 hover:border-primary-400 hover:text-primary-600 shadow-sm"
        title="Añadir bloque"
      >
        <Plus className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div
            className="fixed inset-0 z-20"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          />
          <div
            className="absolute z-30 top-full mt-1 grid grid-cols-4 gap-1 bg-white border border-gray-200 rounded-lg shadow-lg p-2 w-64"
            onClick={(e) => e.stopPropagation()}
          >
            {BLOCK_TYPE_META.map(({ type, label, Icon }) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  onInsert(type);
                  setOpen(false);
                }}
                className="flex flex-col items-center gap-1 p-2 rounded-md hover:bg-primary-50 text-gray-600 hover:text-primary-700"
              >
                <Icon className="w-4 h-4" />
                <span className="text-[10px] leading-tight text-center">{label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ============================================================================
// CANVAS POR TIPO (vista previa; solo Texto es editable directamente aquí)
// ============================================================================

function HeadingCanvas({ block }: { block: HeadingBlock }) {
  const px = HEADING_SIZE_PX[block.size];
  return (
    <p
      style={{
        margin: 0,
        fontWeight: 700,
        fontSize: px,
        lineHeight: `${px + 8}px`,
        color: block.color,
        textAlign: block.align,
      }}
    >
      {block.text || <span className="text-gray-300">Escribe un título…</span>}
    </p>
  );
}

function ButtonCanvas({ block }: { block: ButtonBlock }) {
  return (
    <div style={{ textAlign: block.align }}>
      <span
        style={{
          display: "inline-block",
          width: block.fullWidth ? "100%" : undefined,
          textAlign: "center",
          padding: "14px 28px",
          borderRadius: block.radius,
          backgroundColor: block.bgColor,
          color: block.textColor,
          fontWeight: 700,
          fontSize: 16,
          fontFamily: "Arial, Helvetica, sans-serif",
        }}
      >
        {block.text || "Botón de ejemplo"}
      </span>
    </div>
  );
}

function ImageCanvas({ block }: { block: ImageBlock }) {
  if (!block.src) {
    return (
      <div className="border-2 border-dashed border-gray-300 rounded-lg py-8 text-center text-xs text-gray-400">
        Sin imagen — selecciona el bloque para subir una
      </div>
    );
  }
  return (
    <div style={{ textAlign: block.align }}>
      <img
        src={block.src}
        alt={block.alt}
        style={{ width: `${block.widthPercent}%`, maxWidth: "100%", height: "auto" }}
      />
    </div>
  );
}

function DividerCanvas({ block }: { block: DividerBlock }) {
  return <div style={{ borderTop: `${block.thickness}px solid ${block.color}` }} />;
}

function SpacerCanvas({ block }: { block: SpacerBlock }) {
  return (
    <div style={{ height: block.height }} className="flex items-center justify-center">
      <span className="text-[10px] text-gray-300">{block.height}px</span>
    </div>
  );
}

function HtmlCanvas({ block }: { block: HtmlBlock }) {
  return (
    <div>
      <span className="inline-block mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
        HTML libre
      </span>
      <div
        className="text-sm text-gray-700 [&_img]:max-w-full"
        dangerouslySetInnerHTML={{ __html: block.html }}
      />
    </div>
  );
}

// ============================================================================
// BLOQUE TEXTO (contentEditable + mini-toolbar propia)
// ============================================================================

interface LinkPanelState {
  href: string;
  anchor: HTMLAnchorElement | null;
}

function TextCanvas({
  block,
  onChange,
  variables,
}: {
  block: TextBlock;
  onChange: (patch: Partial<TextBlock>) => void;
  variables: EmailVariableInfo[];
}) {
  const ref = useRef<HTMLDivElement>(null);
  const lastHtmlRef = useRef<string | null>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const [linkPanel, setLinkPanel] = useState<LinkPanelState | null>(null);
  const [varMenuOpen, setVarMenuOpen] = useState(false);
  const urlVariables = variables.filter((v) => v.token.toLowerCase().includes("url"));

  useEffect(() => {
    if (!ref.current) return;
    if (block.html === lastHtmlRef.current) return;
    ref.current.innerHTML = block.html;
    lastHtmlRef.current = block.html;
  }, [block.html]);

  const commit = () => {
    if (!ref.current) return;
    const html = sanitizeInlineHtml(ref.current.innerHTML);
    lastHtmlRef.current = html;
    onChange({ html });
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && ref.current?.contains(sel.anchorNode)) {
      savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (sel && savedRangeRef.current) {
      sel.removeAllRanges();
      sel.addRange(savedRangeRef.current);
    }
  };

  const exec = (command: string) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand(command);
    commit();
  };

  const findEnclosingLink = (): HTMLAnchorElement | null => {
    const sel = window.getSelection();
    let node: Node | null = sel?.anchorNode ?? null;
    while (node && node !== ref.current) {
      if (node instanceof HTMLAnchorElement) return node;
      node = node.parentNode;
    }
    return null;
  };

  const openLinkPanel = () => {
    saveSelection();
    const anchor = findEnclosingLink();
    // Requisito explícito: seleccionar un enlace CON URL guardada muestra
    // siempre esa URL, nunca el campo vacío (bug reportado del editor viejo).
    setLinkPanel({ href: anchor?.getAttribute("href") || "", anchor });
  };

  const applyLink = (href: string) => {
    ref.current?.focus();
    restoreSelection();
    if (linkPanel?.anchor) {
      linkPanel.anchor.setAttribute("href", href);
    } else {
      const sel = window.getSelection();
      if (sel && sel.toString()) {
        document.execCommand("createLink", false, href || "https://");
        const created = findEnclosingLink();
        created?.setAttribute("target", "_blank");
      } else {
        const label = escapeHtmlEntities(href) || "Enlace";
        document.execCommand(
          "insertHTML",
          false,
          `<a href="${escapeHtmlEntities(href)}" target="_blank">${label}</a>`,
        );
      }
    }
    commit();
    setLinkPanel(null);
  };

  const removeLink = () => {
    ref.current?.focus();
    restoreSelection();
    if (linkPanel?.anchor) {
      linkPanel.anchor.replaceWith(...Array.from(linkPanel.anchor.childNodes));
    } else {
      document.execCommand("unlink");
    }
    commit();
    setLinkPanel(null);
  };

  const insertVariable = (token: string) => {
    ref.current?.focus();
    restoreSelection();
    document.execCommand("insertText", false, token);
    commit();
    setVarMenuOpen(false);
  };

  return (
    <div>
      <div className="flex items-center gap-0.5 mb-1.5 pb-1.5 border-b border-gray-100">
        <MiniToolbarBtn title="Negrita" onClick={() => exec("bold")}>
          <Bold className="w-3.5 h-3.5" />
        </MiniToolbarBtn>
        <MiniToolbarBtn title="Cursiva" onClick={() => exec("italic")}>
          <Italic className="w-3.5 h-3.5" />
        </MiniToolbarBtn>
        <div className="relative">
          <MiniToolbarBtn title="Enlace" onClick={openLinkPanel}>
            <LinkIcon className="w-3.5 h-3.5" />
          </MiniToolbarBtn>
          {linkPanel && (
            <LinkPopover
              href={linkPanel.href}
              hasLink={!!linkPanel.anchor}
              variables={urlVariables}
              onApply={applyLink}
              onRemove={linkPanel.anchor ? removeLink : undefined}
              onClose={() => setLinkPanel(null)}
            />
          )}
        </div>
        <div className="relative">
          <MiniToolbarBtn
            title="Insertar variable"
            onClick={() => {
              saveSelection();
              setVarMenuOpen((v) => !v);
            }}
          >
            <span className="text-[11px] font-mono font-semibold px-0.5">{"{ }"}</span>
          </MiniToolbarBtn>
          {varMenuOpen && (
            <div className="absolute z-20 top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-56 overflow-y-auto">
              {variables.map((v) => (
                <button
                  key={v.token}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertVariable(v.token)}
                  className="block w-full text-left px-3 py-1.5 hover:bg-gray-50"
                >
                  <code className="text-xs font-mono text-primary-700">{v.token}</code>
                  <p className="text-[11px] text-gray-500">{v.description}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={commit}
        onBlur={commit}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        className="min-h-[48px] text-[15px] leading-6 text-gray-800 outline-none [&_a]:text-primary-600 [&_a]:underline"
      />
    </div>
  );
}

function MiniToolbarBtn({
  title,
  onClick,
  children,
}: {
  title: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}
      className="p-1.5 rounded hover:bg-gray-100 text-gray-500"
    >
      {children}
    </button>
  );
}

function LinkPopover({
  href,
  hasLink,
  variables,
  onApply,
  onRemove,
  onClose,
}: {
  href: string;
  hasLink: boolean;
  variables: EmailVariableInfo[];
  onApply: (href: string) => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(href);
  return (
    <div className="absolute z-20 top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        URL del enlace
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="https:// o {{variable}}"
        autoFocus
        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500"
      />
      {variables.length > 0 && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {variables.map((v) => (
            <button
              key={v.token}
              type="button"
              title={v.description}
              onClick={() => setValue(v.token)}
              className="text-[11px] font-mono px-1.5 py-0.5 bg-gray-100 hover:bg-primary-50 hover:text-primary-700 rounded"
            >
              {v.token}
            </button>
          ))}
        </div>
      )}
      <div className="mt-2.5 flex items-center justify-between gap-2">
        {hasLink && onRemove ? (
          <button
            type="button"
            onClick={onRemove}
            className="text-xs text-red-600 hover:underline"
          >
            Quitar enlace
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={onClose}
            className="px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded-md"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!value.trim()}
            onClick={() => onApply(value.trim())}
            className="px-2.5 py-1 text-xs bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Aplicar
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PANEL DE PROPIEDADES
// ============================================================================

function PropertiesPanelContent({
  block,
  onChange,
  urlVariables,
}: {
  block: EmailBlock | null;
  onChange: (patch: Partial<EmailBlock>) => void;
  urlVariables: EmailVariableInfo[];
}) {
  if (!block) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        Selecciona un bloque del email para editar sus propiedades.
      </div>
    );
  }

  switch (block.type) {
    case "heading": {
      const set = onChange as (patch: Partial<HeadingBlock>) => void;
      return (
        <div className="p-4 space-y-4">
          <SectionTitle icon={Heading1} label="Título" />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Texto</label>
            <input
              type="text"
              value={block.text}
              onChange={(e) => set({ text: e.target.value })}
              placeholder="Título del email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Tamaño</label>
            <div className="flex gap-1">
              {(["s", "m", "l"] as const).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => set({ size: s })}
                  className={`flex-1 py-1.5 rounded-md border text-xs font-medium ${
                    block.size === s
                      ? "bg-primary-50 border-primary-400 text-primary-700"
                      : "border-gray-200 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {s === "s" ? "Pequeño" : s === "m" ? "Mediano" : "Grande"}
                </button>
              ))}
            </div>
          </div>
          <ColorField label="Color del texto" value={block.color} onChange={(c) => set({ color: c })} />
          <AlignField value={block.align} onChange={(a) => set({ align: a })} />
        </div>
      );
    }
    case "text":
      return (
        <div className="p-4 space-y-2">
          <SectionTitle icon={Type} label="Texto" />
          <p className="text-xs text-gray-500 leading-relaxed">
            Edita el texto directamente en el email. Selecciona una palabra y
            usa la mini barra de herramientas para negrita, cursiva o
            enlaces.
          </p>
        </div>
      );
    case "button": {
      const set = onChange as (patch: Partial<ButtonBlock>) => void;
      return (
        <div className="p-4 space-y-4">
          <SectionTitle icon={MousePointerClick} label="Botón" />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Texto del botón
            </label>
            <input
              type="text"
              value={block.text}
              onChange={(e) => set({ text: e.target.value })}
              placeholder="Reservar ahora"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <UrlField
            label="Enlace"
            value={block.href}
            onChange={(v) => set({ href: v })}
            variables={urlVariables}
          />
          <ColorField
            label="Color de fondo"
            value={block.bgColor}
            onChange={(c) => set({ bgColor: c })}
          />
          <ColorField
            label="Color del texto"
            value={block.textColor}
            onChange={(c) => set({ textColor: c })}
          />
          <AlignField value={block.align} onChange={(a) => set({ align: a })} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Bordes redondeados ({block.radius}px)
            </label>
            <input
              type="range"
              min={0}
              max={24}
              value={block.radius}
              onChange={(e) => set({ radius: Number(e.target.value) })}
              className="w-full"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={block.fullWidth}
              onChange={(e) => set({ fullWidth: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Ancho completo
          </label>
        </div>
      );
    }
    case "image": {
      const set = onChange as (patch: Partial<ImageBlock>) => void;
      return (
        <div className="p-4 space-y-4">
          <SectionTitle icon={ImageIcon} label="Imagen" />
          <ImageUploadField src={block.src} onChange={(src) => set({ src })} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Texto alternativo (alt)
            </label>
            <input
              type="text"
              value={block.alt}
              onChange={(e) => set({ alt: e.target.value })}
              placeholder="Describe la imagen"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Ancho ({block.widthPercent}%)
            </label>
            <input
              type="range"
              min={20}
              max={100}
              step={5}
              value={block.widthPercent}
              onChange={(e) => set({ widthPercent: Number(e.target.value) })}
              className="w-full"
            />
          </div>
          <AlignField value={block.align} onChange={(a) => set({ align: a })} />
          <UrlField
            label="Enlace al hacer clic (opcional)"
            value={block.href}
            onChange={(v) => set({ href: v })}
            variables={urlVariables}
          />
        </div>
      );
    }
    case "divider": {
      const set = onChange as (patch: Partial<DividerBlock>) => void;
      return (
        <div className="p-4 space-y-4">
          <SectionTitle icon={Minus} label="Separador" />
          <ColorField label="Color" value={block.color} onChange={(c) => set({ color: c })} />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Grosor ({block.thickness}px)
            </label>
            <input
              type="range"
              min={1}
              max={8}
              value={block.thickness}
              onChange={(e) => set({ thickness: Number(e.target.value) })}
              className="w-full"
            />
          </div>
        </div>
      );
    }
    case "spacer": {
      const set = onChange as (patch: Partial<SpacerBlock>) => void;
      return (
        <div className="p-4 space-y-4">
          <SectionTitle icon={MoveVertical} label="Espaciador" />
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Altura ({block.height}px)
            </label>
            <input
              type="range"
              min={8}
              max={96}
              step={4}
              value={block.height}
              onChange={(e) => set({ height: Number(e.target.value) })}
              className="w-full"
            />
          </div>
        </div>
      );
    }
    case "html": {
      const set = onChange as (patch: Partial<HtmlBlock>) => void;
      return (
        <div className="p-4 space-y-3">
          <SectionTitle icon={Code2} label="HTML libre" />
          <p className="text-[11px] text-gray-500 leading-relaxed">
            Contenido HTML avanzado (por ejemplo, texto generado con IA o una
            campaña anterior). Edítalo directamente; no se reparte en
            bloques.
          </p>
          <textarea
            value={block.html}
            onChange={(e) => set({ html: e.target.value })}
            rows={12}
            className="w-full px-2.5 py-2 border border-gray-300 rounded-lg text-xs font-mono focus:ring-2 focus:ring-primary-500"
          />
        </div>
      );
    }
  }
}

function SectionTitle({ icon: Icon, label }: { icon: typeof Type; label: string }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-gray-100">
      <Icon className="w-4 h-4 text-primary-600" />
      <span className="text-sm font-semibold text-gray-900">{label}</span>
    </div>
  );
}

function AlignField({
  value,
  onChange,
}: {
  value: BlockAlign;
  onChange: (v: BlockAlign) => void;
}) {
  const options: Array<{ v: BlockAlign; Icon: typeof AlignLeft }> = [
    { v: "left", Icon: AlignLeft },
    { v: "center", Icon: AlignCenter },
    { v: "right", Icon: AlignRight },
  ];
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Alineación</label>
      <div className="flex gap-1">
        {options.map(({ v, Icon }) => (
          <button
            key={v}
            type="button"
            onClick={() => onChange(v)}
            className={`flex-1 flex items-center justify-center py-1.5 rounded-md border ${
              value === v
                ? "bg-primary-50 border-primary-400 text-primary-700"
                : "border-gray-200 text-gray-500 hover:bg-gray-50"
            }`}
          >
            <Icon className="w-4 h-4" />
          </button>
        ))}
      </div>
    </div>
  );
}

const COLOR_PRESETS = [
  BRAND_COLORS.primary,
  BRAND_COLORS.primaryDark,
  "#111111",
  "#333333",
  "#ffffff",
  "#059669",
  "#d97706",
  "#dc2626",
];

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const isValidHex = /^#([0-9a-f]{6})$/i.test(value);
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
        {COLOR_PRESETS.map((c) => (
          <button
            key={c}
            type="button"
            title={c}
            onClick={() => onChange(c)}
            className={`w-6 h-6 rounded-full border ${
              value.toLowerCase() === c.toLowerCase()
                ? "ring-2 ring-offset-1 ring-primary-500"
                : "border-gray-200"
            }`}
            style={{ backgroundColor: c }}
          />
        ))}
        <label
          className="w-6 h-6 rounded-full border border-gray-300 overflow-hidden cursor-pointer relative"
          title="Elegir color"
        >
          <input
            type="color"
            value={isValidHex ? value : "#000000"}
            onChange={(e) => onChange(e.target.value)}
            className="absolute -top-1 -left-1 w-8 h-8 cursor-pointer"
          />
        </label>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#0284c7"
        className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-xs font-mono focus:ring-2 focus:ring-primary-500"
      />
    </div>
  );
}

function UrlField({
  label,
  value,
  onChange,
  variables,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  variables: EmailVariableInfo[];
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="https:// o {{variable}}"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500"
      />
      {open && variables.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg py-1 max-h-48 overflow-y-auto">
          {variables.map((v) => (
            <button
              key={v.token}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(v.token);
                setOpen(false);
              }}
              className="block w-full text-left px-3 py-1.5 hover:bg-gray-50"
            >
              <code className="text-xs font-mono text-primary-700">{v.token}</code>
              <p className="text-[11px] text-gray-500">{v.description}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ImageUploadField({
  src,
  onChange,
}: {
  src: string;
  onChange: (src: string) => void;
}) {
  const [tab, setTab] = useState<"upload" | "url">("upload");
  const [error, setError] = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  // Nunca se llama a `/email-campaigns/upload-image` desde aquí: se guarda el
  // `data:` URI directamente en el bloque. El propio guardado de la campaña
  // (`EmailCampaigns.tsx` → `handleSave`) ya sustituye cualquier `<img
  // src="data:...">` por su URL alojada y sincroniza `form.htmlContent` con
  // la respuesta — reabrir la campaña vuelve a leer esa URL real del DOM al
  // parsear (nunca de un JSON aparte), así que no hace falta duplicar aquí
  // la lógica de subida.
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Solo se permiten archivos de imagen");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("La imagen no puede superar 5 MB");
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl === "string") onChange(dataUrl);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Imagen</label>
      {src && (
        <img
          src={src}
          alt=""
          className="w-full max-h-32 object-contain border border-gray-200 rounded-lg mb-2 bg-gray-50"
        />
      )}
      <div className="flex border border-gray-200 rounded-lg mb-2 overflow-hidden text-xs">
        <button
          type="button"
          onClick={() => setTab("upload")}
          className={`flex-1 py-1.5 ${tab === "upload" ? "bg-primary-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        >
          Subir
        </button>
        <button
          type="button"
          onClick={() => setTab("url")}
          className={`flex-1 py-1.5 ${tab === "url" ? "bg-primary-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"}`}
        >
          URL
        </button>
      </div>
      {tab === "upload" ? (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFile}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-lg py-4 text-xs text-gray-500 hover:border-primary-400 hover:text-primary-600 hover:bg-primary-50"
          >
            <Upload className="w-4 h-4" /> Seleccionar imagen
          </button>
          {error && <p className="text-[11px] text-red-600 mt-1">{error}</p>}
        </>
      ) : (
        <input
          type="text"
          value={src.startsWith("data:") ? "" : src}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://..."
          className="w-full px-2.5 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary-500"
        />
      )}
    </div>
  );
}
